import React, { useEffect, useState, useCallback } from "react";
import useApi from "@/hooks/useApi";

// ── Types ──────────────────────────────────────────────────────────────────

interface ConversionGoal {
  id: number;
  name: string;
  triggerType: string;
  triggerUrl?: string;
  eventName?: string;
  revenueVariableName?: string;
  defaultAmount: number;
  allowOnce: boolean;
  attributionWindowHours: number;
  isEnabled: boolean;
  createdAt: string;
}

type WizardStep = "type" | "configure" | "attribution" | "deploy";

const TRIGGER_TYPES = [
  {
    key: "url",
    label: "URL Visit",
    icon: "fas fa-link",
    description: "Fire when a visitor lands on a specific page (e.g. /checkout/success)",
    color: "border-violet-300 bg-violet-50 text-violet-700"
  },
  {
    key: "shopify_order",
    label: "Shopify Order",
    icon: "fab fa-shopify",
    description: "Auto-fires when a Shopify order is placed — requires Shopify integration",
    color: "border-emerald-300 bg-emerald-50 text-emerald-700",
    badge: "Integration"
  },
  {
    key: "square_payment",
    label: "Square Payment",
    icon: "fas fa-square",
    description: "Auto-fires on Square transaction completion — requires Square integration",
    color: "border-blue-300 bg-blue-50 text-blue-700",
    badge: "Integration"
  },
  {
    key: "bc_order",
    label: "BigCommerce Order",
    icon: "fas fa-shopping-bag",
    description: "Tracks BigCommerce order completions automatically",
    color: "border-indigo-300 bg-indigo-50 text-indigo-700",
    badge: "Integration"
  },
  {
    key: "woo_order",
    label: "WooCommerce Order",
    icon: "fas fa-wordpress",
    description: "Tracks WooCommerce purchase events",
    color: "border-purple-300 bg-purple-50 text-purple-700",
    badge: "Integration"
  },
  {
    key: "custom_event",
    label: "Custom JS Event",
    icon: "fas fa-code",
    description: "Fire manually with velaro('trackConversion', …) from any script",
    color: "border-slate-300 bg-slate-50 text-slate-700"
  }
];

const ATTRIBUTION_WINDOWS = [
  { hours: 1, label: "1 hour" },
  { hours: 24, label: "24 hours" },
  { hours: 168, label: "7 days" },
  { hours: 720, label: "30 days" }
];

const TRIGGER_LABELS: Record<string, string> = {
  url: "URL Visit",
  shopify_order: "Shopify Order",
  square_payment: "Square Payment",
  bc_order: "BigCommerce Order",
  woo_order: "WooCommerce Order",
  magento_order: "Magento Order",
  custom_event: "Custom Event"
};

const fmt$ = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n);

// ── Wizard ─────────────────────────────────────────────────────────────────

interface WizardState {
  name: string;
  triggerType: string;
  triggerUrl: string;
  eventName: string;
  revenueVariableName: string;
  defaultAmount: number;
  allowOnce: boolean;
  attributionWindowHours: number;
}

function WizardModal({
  onClose,
  onSaved,
  editing
}: {
  onClose: () => void;
  onSaved: () => void;
  editing?: ConversionGoal;
}) {
  const api = useApi();
  const [step, setStep] = useState<WizardStep>(editing ? "configure" : "type");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<WizardState>({
    name: editing?.name ?? "",
    triggerType: editing?.triggerType ?? "url",
    triggerUrl: editing?.triggerUrl ?? "",
    eventName: editing?.eventName ?? "",
    revenueVariableName: editing?.revenueVariableName ?? "",
    defaultAmount: editing?.defaultAmount ?? 0,
    allowOnce: editing?.allowOnce ?? false,
    attributionWindowHours: editing?.attributionWindowHours ?? 24
  });

  const set = (k: keyof WizardState, v: any) => setForm(f => ({ ...f, [k]: v }));
  const isIntegration = ["shopify_order", "square_payment", "bc_order", "woo_order", "magento_order"].includes(form.triggerType);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name,
        triggerType: form.triggerType,
        triggerUrl: form.triggerUrl || null,
        eventName: form.eventName || null,
        revenueVariableName: form.revenueVariableName || null,
        defaultAmount: form.defaultAmount,
        allowOnce: form.allowOnce,
        attributionWindowHours: form.attributionWindowHours,
        isEnabled: true
      };
      if (editing) {
        await api.admin.put(`Conversions/goals/${editing.id}`, body);
      } else {
        await api.admin.post("Conversions/goals", body);
      }
      onSaved();
    } catch {
      // keep modal open on error
    } finally {
      setSaving(false);
    }
  };

  const steps: { key: WizardStep; label: string }[] = [
    { key: "type", label: "Goal Type" },
    { key: "configure", label: "Configure" },
    { key: "attribution", label: "Attribution" },
    { key: "deploy", label: "Deploy" }
  ];

  const stepIdx = steps.findIndex(s => s.key === step);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              {editing ? "Edit Conversion Goal" : "New Conversion Goal"}
            </h2>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
              Define what counts as a conversion and how to attribute it to a chat
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
            <i className="fas fa-times text-lg" />
          </button>
        </div>

        {/* Step indicator */}
        {!editing && (
          <div className="flex items-center gap-0 px-6 py-3 border-b border-slate-100 dark:border-slate-700">
            {steps.map((s, i) => (
              <React.Fragment key={s.key}>
                <div className={`flex items-center gap-1.5 text-xs font-medium ${i <= stepIdx ? "text-violet-600 dark:text-violet-400" : "text-slate-400"}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${i < stepIdx ? "bg-violet-600 text-white" : i === stepIdx ? "border-2 border-violet-600 text-violet-600" : "border border-slate-300 text-slate-400"}`}>
                    {i < stepIdx ? <i className="fas fa-check" /> : i + 1}
                  </div>
                  {s.label}
                </div>
                {i < steps.length - 1 && <div className="flex-1 h-px bg-slate-200 dark:bg-slate-600 mx-2" />}
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="p-6 space-y-5">

          {/* Step: Type */}
          {step === "type" && (
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-3">What should trigger this conversion?</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {TRIGGER_TYPES.map(t => (
                  <button
                    key={t.key}
                    onClick={() => set("triggerType", t.key)}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${form.triggerType === t.key ? t.color + " border-opacity-100 shadow-sm" : "border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"}`}
                  >
                    <i className={`${t.icon} text-base mt-0.5`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-semibold">{t.label}</span>
                        {t.badge && (
                          <span className="text-[10px] bg-white/60 dark:bg-slate-700 px-1.5 py-0.5 rounded-full font-medium">
                            {t.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-xs opacity-75 mt-0.5 leading-snug">{t.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step: Configure */}
          {step === "configure" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Goal Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => set("name", e.target.value)}
                  placeholder="e.g. Purchase Completed"
                  className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>

              {form.triggerType === "url" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Conversion URL</label>
                  <input
                    type="text"
                    value={form.triggerUrl}
                    onChange={e => set("triggerUrl", e.target.value)}
                    placeholder="https://yourdomain.com/checkout/success"
                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    The widget fires this goal when a visitor with an active or recent chat session visits this URL.
                  </p>
                </div>
              )}

              {form.triggerType === "custom_event" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Event Name</label>
                  <input
                    type="text"
                    value={form.eventName}
                    onChange={e => set("eventName", e.target.value)}
                    placeholder="e.g. purchase_complete"
                    className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    Used to identify this goal when calling <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">velaro('trackConversion', {'{ goalId: … }'})</code>
                  </p>
                </div>
              )}

              {isIntegration && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <i className="fas fa-plug text-emerald-500 mt-0.5" />
                  <div className="text-sm text-emerald-700 dark:text-emerald-300">
                    <strong>Auto-connected.</strong> This goal fires automatically whenever a {TRIGGER_LABELS[form.triggerType]} event occurs on your account. No extra code required — just make sure the integration is active.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: Attribution */}
          {step === "attribution" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Attribution Window</label>
                <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">
                  How long after a chat conversation should a conversion be attributed to that chat?
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {ATTRIBUTION_WINDOWS.map(w => (
                    <button
                      key={w.hours}
                      onClick={() => set("attributionWindowHours", w.hours)}
                      className={`py-2.5 rounded-lg text-sm font-medium border transition-all ${form.attributionWindowHours === w.hours ? "bg-violet-600 text-white border-violet-600 shadow-sm" : "border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:border-violet-300 dark:hover:border-violet-500"}`}
                    >
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Default Revenue Amount</label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 dark:text-slate-400 text-sm">$</span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.defaultAmount}
                    onChange={e => set("defaultAmount", parseFloat(e.target.value) || 0)}
                    className="w-36 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  Used when no dynamic amount is provided. Set to 0 for non-revenue goals.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Revenue Variable Name <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  type="text"
                  value={form.revenueVariableName}
                  onChange={e => set("revenueVariableName", e.target.value)}
                  placeholder="e.g. order_total"
                  className="w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  JS variable read by the widget to capture the actual order amount.
                </p>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.allowOnce}
                  onChange={e => set("allowOnce", e.target.checked)}
                  className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm text-slate-700 dark:text-slate-200">Count each visitor only once for this goal</span>
              </label>
            </div>
          )}

          {/* Step: Deploy */}
          {step === "deploy" && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                <i className="fas fa-check-circle text-emerald-500 text-lg mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">You're all set!</p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                    Your goal <strong>{form.name}</strong> is ready. {isIntegration ? "It will fire automatically when your integration receives an event." : "Add the snippet below to your page to start tracking."}
                  </p>
                </div>
              </div>

              {!isIntegration && (
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Add this snippet to your page</p>
                  <pre className="bg-slate-900 text-emerald-400 text-xs rounded-lg p-4 overflow-x-auto leading-relaxed">
{form.triggerType === "url"
  ? `<!-- The Velaro widget fires this goal automatically when
     a visitor with a chat session visits the URL:
     ${form.triggerUrl || "(your conversion URL)"}
     No extra code needed — just keep the widget on that page. -->`
  : `<script>
  // Add to your ${form.name} confirmation page
  if (window.velaro) {
    velaro('trackConversion', {
      goalId: YOUR_GOAL_ID,  // replace with the ID shown below
      amount: 0.00           // optional: actual order amount
    });
  }
</script>`}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-700">
          <div>
            {!editing && stepIdx > 0 && (
              <button
                onClick={() => setStep(steps[stepIdx - 1].key)}
                className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              >
                ← Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
              Cancel
            </button>
            {step !== "deploy" && !editing && (
              <button
                onClick={() => setStep(steps[stepIdx + 1].key)}
                disabled={step === "configure" && !form.name.trim()}
                className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
              >
                Next →
              </button>
            )}
            {(step === "deploy" || editing) && (
              <button
                onClick={save}
                disabled={saving || !form.name.trim()}
                className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                {saving && <i className="fas fa-spinner fa-spin text-xs" />}
                {editing ? "Save Changes" : "Create Goal"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function Conversions() {
  const api = useApi();
  const [goals, setGoals] = useState<ConversionGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<ConversionGoal | undefined>();
  const [deleting, setDeleting] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await (await api.admin.get("Conversions/goals")).json() as ConversionGoal[];
      setGoals(Array.isArray(data) ? data : []);
    } catch {
      setGoals([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const deleteGoal = async (id: number) => {
    if (!confirm("Delete this conversion goal? All recorded events for it will also be deleted.")) return;
    setDeleting(id);
    try {
      await api.admin.delete(`Conversions/goals/${id}`);
      setGoals(g => g.filter(x => x.id !== id));
    } catch {
      alert("Failed to delete goal");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Conversion Goals</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Define what actions count as conversions and attribute them to chat sessions
          </p>
        </div>
        <button
          onClick={() => { setEditingGoal(undefined); setWizardOpen(true); }}
          className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-medium text-sm px-4 py-2.5 rounded-lg transition-colors"
        >
          <i className="fas fa-plus text-xs" />
          New Goal
        </button>
      </div>

      {/* Goals list */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <i className="fas fa-spinner fa-spin text-2xl text-violet-500" />
        </div>
      ) : goals.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-white dark:bg-slate-800 border border-dashed border-slate-300 dark:border-slate-600 rounded-2xl">
          <div className="w-14 h-14 bg-violet-100 dark:bg-violet-900/30 rounded-2xl flex items-center justify-center mb-4">
            <i className="fas fa-bullseye text-2xl text-violet-500" />
          </div>
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-1">No goals yet</h2>
          <p className="text-sm text-slate-400 dark:text-slate-500 max-w-sm mb-5">
            Create a conversion goal to start measuring how chat drives purchases, sign-ups, and business outcomes.
          </p>
          <button
            onClick={() => { setEditingGoal(undefined); setWizardOpen(true); }}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-medium text-sm px-5 py-2.5 rounded-lg transition-colors"
          >
            <i className="fas fa-plus text-xs" />
            Create first goal
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {goals.map(g => (
            <div key={g.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex items-start gap-4 shadow-sm hover:shadow-md transition-shadow">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${g.isEnabled ? "bg-violet-100 dark:bg-violet-900/30" : "bg-slate-100 dark:bg-slate-700"}`}>
                <i className={`fas fa-bullseye text-sm ${g.isEnabled ? "text-violet-500" : "text-slate-400"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800 dark:text-slate-100">{g.name}</span>
                  {!g.isEnabled && (
                    <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-500 px-2 py-0.5 rounded-full">Paused</span>
                  )}
                  <span className="text-xs bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 px-2 py-0.5 rounded-full">
                    {TRIGGER_LABELS[g.triggerType] ?? g.triggerType}
                  </span>
                  <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-500 px-2 py-0.5 rounded-full">
                    {ATTRIBUTION_WINDOWS.find(w => w.hours === g.attributionWindowHours)?.label ?? `${g.attributionWindowHours}h window`}
                  </span>
                </div>
                <div className="text-xs text-slate-400 dark:text-slate-500 mt-1 flex flex-wrap gap-3">
                  {g.triggerUrl && <span><i className="fas fa-link mr-1" />{g.triggerUrl}</span>}
                  {g.eventName && <span><i className="fas fa-code mr-1" />{g.eventName}</span>}
                  {g.defaultAmount > 0 && <span><i className="fas fa-dollar-sign mr-1" />{fmt$(g.defaultAmount)} default</span>}
                  {g.revenueVariableName && <span><i className="fas fa-variable mr-1" />var: {g.revenueVariableName}</span>}
                  {g.allowOnce && <span><i className="fas fa-user-check mr-1" />once per visitor</span>}
                  <span>ID: {g.id}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => { setEditingGoal(g); setWizardOpen(true); }}
                  className="p-2 text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                  title="Edit"
                >
                  <i className="fas fa-pencil text-sm" />
                </button>
                <button
                  onClick={() => deleteGoal(g.id)}
                  disabled={deleting === g.id}
                  className="p-2 text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                  title="Delete"
                >
                  {deleting === g.id
                    ? <i className="fas fa-spinner fa-spin text-sm" />
                    : <i className="fas fa-trash text-sm" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Attribution info banner */}
      <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex items-start gap-3">
        <i className="fas fa-info-circle text-violet-400 mt-0.5" />
        <div className="text-sm text-slate-600 dark:text-slate-300">
          <strong>How attribution works:</strong> A conversion is attributed to a chat when the visitor had an active or recent conversation within the goal's attribution window before the conversion occurred.
          View attributed revenue in{" "}
          <a href="/reports/conversions" className="text-violet-600 dark:text-violet-400 underline">Reports → Conversions</a>.
        </div>
      </div>

      {wizardOpen && (
        <WizardModal
          onClose={() => setWizardOpen(false)}
          onSaved={() => { setWizardOpen(false); load(); }}
          editing={editingGoal}
        />
      )}
    </div>
  );
}
